import React, { useCallback, useState, createContext, useContext } from 'react';
export type Language = 'en' | 'es';
interface I18nContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}
const I18nContext = createContext<I18nContextType>({
  lang: 'en',
  setLang: () => {},
  t: (key: string) => key
});
export function useI18n() {
  return useContext(I18nContext);
}
export function I18nProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lang, setLang] = useState<Language>('en');
  const t = useCallback((key: string): string => {
    const dict = lang === 'es' ? es : en;
    return (dict as any)[key] ?? (en as any)[key] ?? key;
  }, [lang]);
  return <I18nContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-1901">
      {children}
    </I18nContext.Provider>;
}
// ── Helper: append language instruction to AI prompts ────────────────────────
export function withLanguage(prompt: string, lang: Language): string {
  if (lang === 'es') {
    return `${prompt}\n\nIMPORTANT: All text content in your response (titles, descriptions, steps, hazards, controls, conditions, names, responsibilities, etc.) MUST be written entirely in Spanish. JSON keys must remain in English. Only the VALUES should be in Spanish.`;
  }
  return prompt;
}
// ═══════════════════════════════════════════════════════════════════════════════
// TRANSLATIONS
// ═══════════════════════════════════════════════════════════════════════════════
const en: Record<string, string> = {
  // ── Global / Navigation ──
  back: 'Back',
  continue: 'Continue',
  close: 'Close',
  dismiss: 'Dismiss',
  save: 'Save',
  cancel: 'Cancel',
  add: 'Add',
  delete: 'Delete',
  edit: 'Edit',
  yes: 'Yes',
  no: 'No',
  loading: 'Loading…',
  // ── Phase Labels ──
  'phase.taskMop': 'Task Description & MOP',
  'phase.conditions': 'Conditions',
  'phase.crew': 'Crew',
  'phase.hazards': 'Hazards',
  'phase.controls': 'Controls',
  'phase.finalization': 'Finalization',
  // ── Loading Messages ──
  'loading.analyzingTask': 'Analyzing task description…',
  'loading.analyzingTaskSub': 'AI is generating MOP steps and hazards\nPowered by Your Company Knowledge Base',
  'loading.identifyingHazards': 'Identifying hazards…',
  'loading.identifyingHazardsSub': 'AI is analyzing steps and conditions\nPowered by Your Company Knowledge Base',
  'loading.generatingControls': 'Generating controls…',
  'loading.generatingControlsSub': 'AI is building safety controls for approved hazards\nPowered by Your Company Knowledge Base',
  'loading.analyzingPTP': 'Analyzing Pre-Task Plan…',
  'loading.analyzingPTPSub': 'AI is identifying key risks and safety priorities\nPowered by Your Company Knowledge Base',
  'loading.creatingPTP': 'Creating Pre-Task Plan…',
  'loading.creatingPTPSub': 'Compiling safety document\nPowered by Your Company Knowledge Base',
  'loading.analyzingMeeting': 'Analyzing meeting recording…',
  'loading.analyzingMeetingSub': 'Evaluating crew discussion quality\nPowered by Your Company Knowledge Base',
  'loading.extractingInfo': 'Extracting new information…',
  'loading.extractingInfoSub': 'Comparing meeting notes to PTP\nPowered by Your Company Knowledge Base',
  'loading.updatingPTP': 'Updating Pre-Task Plan…',
  'loading.updatingPTPSub': 'Incorporating meeting findings\nPowered by Your Company Knowledge Base',
  'loading.finalizingPTP': 'Finalizing Pre-Task Plan…',
  'loading.finalizingPTPSub': 'Saving completed plan\nPowered by Your Company Knowledge Base',
  'loading.analyzingConditions': 'Analyzing task conditions…',
  'loading.analyzingConditionsSub': 'AI is identifying relevant E/C/M conditions based on your task',
  'loading.analyzingPermits': 'Analyzing required permits…',
  'loading.analyzingPermitsSub': 'AI is identifying permits based on your task\nPowered by Your Company Knowledge Base',
  // ── New PTP Screen ──
  'newPtp.title': 'New Pre-Task Plan',
  'newPtp.subtitle': 'Start a new safety plan for your crew',
  'newPtp.project': 'Project',
  'newPtp.location': 'Location',
  'newPtp.date': 'Date',
  'newPtp.startPlan': 'Start Plan',
  'newPtp.poweredBy': 'Powered by Your Company Knowledge Base',
  'newPtp.whatToExpect': 'What to expect',
  'newPtp.step1': 'Describe the task using your voice',
  'newPtp.step2': 'Review MOP steps, equipment & permits',
  'newPtp.step3': 'Assess work environment conditions',
  'newPtp.step4': 'Assign crew roles',
  'newPtp.step5': 'Identify hazards and control measures',
  'newPtp.step6': 'Review and finalize PTP',
  // ── Voice Description Screen ──
  'voice.title': 'Describe the Work',
  'voice.subtitle': "Describe the task, where it will take place, and when it's scheduled. Include site conditions and how the work will be carried out.",
  'voice.example': 'e.g. "We\'re installing steel beams on Level 2 east wing, starting at 7 AM, using the mobile crane…"',
  'voice.placeholder': 'Describe the task, location, time, and key activities...',
  'voice.analyzeTask': 'Analyze Task',
  'voice.tapToRecord': 'Tap to start recording',
  'voice.tapToStop': 'Listening… tap to stop',
  'voice.tapToReRecord': 'Tap to re-record',
  'voice.analyzing': 'Analyzing…',
  'voice.modeVoice': 'Voice',
  'voice.modeType': 'Type',
  'voice.modeUpload': 'Upload',
  'voice.missingTitle': 'Missing information',
  'voice.missingPrefix': 'Your description is missing:',
  'voice.missingSuffix': '. Add more details for a better PTP.',
  'voice.addMoreInfo': 'Add More Info',
  'voice.missingTask': 'task description',
  'voice.missingLocation': 'location',
  'voice.missingTime': 'time / schedule',
  // ── Task Understanding Screen ──
  'task.mopSteps': 'MOP Steps',
  'task.crew': 'Crew',
  'task.addStep': 'Add step',
  'task.voiceCorrection': 'Describe changes to the MOP steps — add, remove, reorder, or',
  'task.loadingSteps': 'AI is identifying the task type and generating MOP steps',
  // ── Conditions Intro Screen ──
  'condIntro.title': 'Task Conditions',
  'condIntro.subtitle': "Before identifying hazards, let's assess the conditions using the ECM framework.",
  'condIntro.environment': 'Environment',
  'condIntro.environmentDesc': 'Physical site conditions, weather, layout, nearby activity',
  'condIntro.capability': 'Capability',
  'condIntro.capabilityDesc': 'Crew experience, training, certifications, familiarity',
  'condIntro.motivation': 'Motivation',
  'condIntro.motivationDesc': 'Schedule pressure, workload, deadlines, task repetitiveness',
  'condIntro.continue': 'Review Conditions',
  // ── Work Environment Screen ──
  'workEnv.title': 'Task Conditions',
  'workEnv.subtitle': 'Review each condition. Mark as relevant or not relevant to this task.',
  'workEnv.reviewed': 'reviewed',
  'workEnv.addCondition': 'Add condition',
  'workEnv.relevant': 'Relevant',
  'workEnv.notRelevant': 'Not Relevant',
  // ── Crew Assignment Screen ──
  'crew.title': 'Crew Assignment',
  'crew.subtitle': 'Review and adjust the crew for this task.',
  'crew.size': 'Crew Size',
  'crew.name': 'Name',
  'crew.role': 'Role',
  'crew.responsibility': 'Responsibility',
  // ── Permits Screen ──
  'permits.title': 'Permits Required',
  'permits.subtitle': 'Review the permits required for this task.',
  'permits.noPermits': 'No permits listed',
  'permits.addPermit': 'Add permit',
  'permits.editPermit': 'Edit Permit',
  'permits.voiceDesc': 'Describe any additional permits required for this task.',
  'permits.voiceCorrect': 'Voice Correct',
  'permits.extracting': 'Extracting permits…',
  'permits.extracted': 'Permits extracted!',
  'permits.analyzing': 'Analyzing task for permits…',
  'permits.analyzingSub': 'AI is determining required permits based on the task and equipment.',
  'permits.needed': 'Needed',
  'permits.notNeeded': 'Not Needed',
  'permits.permitStatus': 'Status:',
  'permits.obtained': 'Obtained',
  'permits.missing': 'Missing',
  'permits.missingWarning': 'Missing Required Permits',
  // ── Hazard Review Screen ──
  'hazard.title': 'Review Hazards',
  'hazard.subtitle': 'Review each hazard. Mark as relevant or not relevant.',
  'hazard.reviewed': 'reviewed',
  'hazard.generalHazards': 'General Site Hazards',
  'hazard.addHazard': 'Add hazard',
  // ── Step Hazard / Controls Screen ──
  'controls.title': 'Controls',
  'controls.eliminateSub': 'Eliminate / Substitute',
  'controls.engineering': 'Engineering',
  'controls.adminTraining': 'Admin / Training',
  'controls.ppe': 'PPE',
  'controls.equipment': 'Equipment',
  // ── General Hazards Screen ──
  'general.title': 'General Hazards & Equipment',
  'general.equipment': 'General Equipment',
  // ── PTP Summary Screen ──
  'summary.title': 'PTP Summary',
  'summary.subtitle': 'Review your Pre-Task Plan before creating.',
  'summary.createPTP': 'Create PTP',
  'summary.mop': 'Method of Procedure (MOP)',
  'summary.conditions': 'Conditions',
  'summary.crew': 'Crew',
  'summary.hazards': 'Hazards',
  'summary.controls': 'Controls',
  'summary.equipment': 'Equipment',
  // ── PTP Created / Complete ──
  'created.title': 'PTP Created',
  'created.subtitle': 'Your Pre-Task Plan is ready.',
  'created.startMeeting': 'Start Meeting Recording',
  'created.exportPTP': 'Export PTP',
  'created.qualityScore': 'Quality Score',
  // ── Recording ──
  'recording.title': 'Meeting Recording',
  'recording.stop': 'Stop Recording',
  'recording.pause': 'Pause',
  'recording.resume': 'Resume',
  'recording.participants': 'Participants',
  'recording.topicsCovered': 'Topics Covered',
  // ── Quality Score ──
  'quality.title': 'Meeting Quality',
  'quality.topicCoverage': 'Topic Coverage',
  'quality.participation': 'Crew Participation',
  'quality.duration': 'Meeting Duration',
  // ── New Info ──
  'newInfo.title': 'New Information',
  'newInfo.subtitle': 'The AI found new information from the meeting not in the original PTP.',
  // ── Updated PTP ──
  'updatedPtp.title': 'Updated Pre-Task Plan',
  'updatedPtp.mopSteps': 'MOP Steps — Hazards & Controls',
  'updatedPtp.continueQuiz': 'Continue to Quiz',
  'updatedPtp.exportPTP': 'Export PTP',
  'updatedPtp.meetingReview': 'Meeting Review',
  // ── Quiz ──
  'quiz.sendTitle': 'Send Quiz to Crew',
  'quiz.sendButton': 'Send Quiz',
  'quiz.takeQuiz': 'Take Quiz as Foreman',
  'quiz.title': 'PTP Safety Quiz',
  'quiz.complete': 'Quiz Complete',
  'quiz.next': 'Next',
  'quiz.confirm': 'Confirm',
  // ── Crew Results ──
  'results.title': 'Crew Quiz Results',
  'results.crewReadiness': 'Crew Readiness',
  'results.finalize': 'Finalize PTP',
  'results.reviewPTP': 'Review Updated PTP',
  'results.repeatQuiz': 'Repeat Safety Quiz',
  // ── PTP List ──
  'list.title': 'Pre-Task Plans',
  'list.plans': 'Plans',
  'list.dashboard': 'Dashboard',
  'list.createNew': 'Create New PTP',
  'list.totalPtps': 'Total PTPs',
  'list.completed': 'Completed',
  'list.avgScore': 'Avg Score',
  'status.jobDone': 'Job Done',
  'status.ptpComplete': 'PTP Complete',
  'status.inProgress': 'In Progress',
  'status.draft': 'Draft',
  'list.backToPlans': 'Back to Plans',
  'list.contractor': 'Contractor',
  'list.workers': 'workers',
  'list.meetingScore': 'Meeting Score',
  'list.ptpProgress': 'PTP Progress',
  'list.ptpCreated': 'Pre-Task Plan created',
  'list.meetingRecorded': 'PTP Meeting recorded',
  'list.quizCompleted': 'Crew quiz completed',
  'list.taskCompleted': 'Task completed & post-job report',
  'list.needsMeetingMsg': "This PTP hasn't been discussed with the crew yet. Record the meeting to verify understanding and capture additional safety information.",
  'list.needsQuizMsg': 'The meeting has been recorded. Send the crew quiz to verify everyone understands the safety plan.',
  'list.canCompleteMsg': 'PTP process is complete. Once the job is finished, mark it as completed to file a post-job report with AI safety analysis.',
  'list.jobDoneMsg': 'This task has been completed and the post-job report has been filed. AI analysis is available for review.',
  'list.recordMeeting': 'Record PTP Meeting',
  'list.sendQuiz': 'Send Crew Quiz',
  'list.markComplete': 'Mark Task Completed',
  'list.viewFullPtp': 'View Full PTP',
  'list.jobsDone': 'Jobs Done',
  'list.justCompleted': 'Just completed',
  'list.meetingNotRecorded': 'Meeting not recorded',
  'list.readyForCompletion': 'Ready for task completion',
  // ── Dashboard ──
  'dash.title': 'Project Dashboard',
  'dash.projectSummary': 'Project Summary',
  'dash.contractors': 'Contractors',
  'dash.avgQuality': 'Avg Quality',
  'dash.meetingQuality': 'Meeting Quality',
  'dash.quizCompletion': 'Quiz Completion',
  'dash.projectOverview': 'Project Overview',
  'dash.last14Days': 'Last 14 Days',
  'dash.avgPtpQuality': 'Average PTP Quality Score',
  'dash.downward': 'Downward',
  'dash.qualityExplanation': 'Driven by declining meeting quality and quiz completion in lower-performing contractors',
  'dash.activeContractors': 'Active Contractors',
  'dash.contractorPerformance': 'Contractor Performance',
  'dash.hazardCoverage': 'Hazard Coverage',
  'dash.controlQuality': 'Control Quality',
  'dash.crewParticipation': 'Crew Participation',
  'dash.updateRate': 'Update Rate',
  'dash.ptpsSubmitted': 'PTPs Submitted',
  'dash.trendDetail': 'Trend Detail',
  'dash.aiInsight': 'AI Insight',
  'dash.suggestedAction': 'Suggested Action',
  // ── Nearby Task Alert ──
  'nearby.title': 'Nearby Activity Detected',
  'nearby.subtitle': 'Another task is scheduled nearby at the same time',
  'nearby.addToConditions': 'Add to Conditions',
  'nearby.willBeAdded': 'This will be added as an Environment condition in your PTP to ensure proper coordination.',
  // ── Export ──
  'export.preview': 'Preview Pre-Shift',
  'export.close': 'Close',
  // ── Signature ──
  'signature.title': 'Foreman Signature',
  'signature.disclaimer': 'By signing below, I confirm that I have reviewed this Pre-Task Plan, that all identified hazards and controls are accurate, and that I will communicate this plan to all crew members before work begins.',
  'signature.placeholder': 'Sign here',
  'signature.clear': 'Clear',
  'signature.required': 'Signature required to finalize',
  'signature.finalize': 'Finalize & Continue',
  'signature.editPlan': 'Edit Plan',
  'signature.exportPdf': 'Export PDF',
  // ── Post-Job Review ──
  'postJob.title': 'Post-Job Report',
  // ── Date Labels ──
  'date.today': 'Today',
  'date.yesterday': 'Yesterday',
  // ── Status Labels ──
  'status.all': 'All',
  'status.draft': 'Draft',
  'status.ready': 'Ready',
  'status.signed': 'Signed',
  'status.jobDone': 'Job Done',
  // ── Inline Labels ──
  'list.results': 'Results',
  'list.noRecording': 'No Recording',
  // ── Crew Screen ──
  'crew.addWorker': 'Add Worker',
  'crew.noMatch': 'No workers match',
  'crew.searchPlaceholder': 'Search by name or role...',
  'crew.available': 'Available Workers',
  'crew.assigned': 'Assigned',
  'crew.added': 'Added',
  // ── Equipment Screen ──
  'equip.title': 'Equipment & Tools',
  'equip.noEquipment': 'No equipment listed',
  'equip.addItem': 'Add item',
  'equip.editEquipment': 'Edit Equipment',
  'equip.voiceDesc': 'Describe the tools and equipment needed.',
  'equip.voiceCorrect': 'Voice Correct',
  'equip.regenerate': 'Regenerate',
  'equip.extracting': 'Extracting equipment…',
  'equip.extracted': 'Equipment extracted!',
  // ── Permits Screen ──
  'permits.title': 'Permits Required',
  'permits.subtitle': 'Review the permits required for this task.',
  'permits.noPermits': 'No permits listed',
  'permits.addPermit': 'Add permit',
  'permits.editPermit': 'Edit Permit',
  'permits.voiceDesc': 'Describe any additional permits required for this task.',
  'permits.voiceCorrect': 'Voice Correct',
  'permits.extracting': 'Extracting permits…',
  'permits.extracted': 'Permits extracted!',
  'permits.analyzing': 'Analyzing task for permits…',
  'permits.analyzingSub': 'AI is determining required permits based on the task and equipment.',
  'permits.needed': 'Needed',
  'permits.notNeeded': 'Not Needed',
  'permits.permitStatus': 'Status:',
  'permits.obtained': 'Obtained',
  'permits.missing': 'Missing',
  'permits.missingWarning': 'Missing Required Permits',
  // ── Hazard Screen ──
  'hazard.addHazards': 'Add Hazards',
  'hazard.editHazard': 'Edit Hazard',
  'hazard.describePlaceholder': 'Describe the hazard...',
  'hazard.voiceDesc': "Describe hazards you've identified. Mention the step number if applicable.",
  'hazard.noHazards': 'No hazards for this step',
  'hazard.extracted': 'Hazards extracted!',
  // ── Summary Screen ──
  'summary.keyPoints': 'Key Points',
  'summary.aiComplete': 'AI analysis complete',
  // ── Recording Screen ──
  'recording.listening': 'Listening...',
  'recording.aiNote': 'AI Note',
  'recording.meetingActive': 'Meeting is active. Topics will be tracked as they are discussed.',
  'recording.pending': 'Pending',
  // ── General ──
  'general.none': 'None',
  'general.step': 'Step'
};
const es: Record<string, string> = {
  // ── Global / Navigation ──
  back: 'Atrás',
  continue: 'Continuar',
  close: 'Cerrar',
  dismiss: 'Descartar',
  save: 'Guardar',
  cancel: 'Cancelar',
  add: 'Agregar',
  delete: 'Eliminar',
  edit: 'Editar',
  yes: 'Sí',
  no: 'No',
  loading: 'Cargando…',
  // ── Phase Labels ──
  'phase.taskMop': 'Descripción y MOP',
  'phase.conditions': 'Condiciones',
  'phase.crew': 'Cuadrilla',
  'phase.hazards': 'Peligros',
  'phase.controls': 'Controles',
  'phase.finalization': 'Finalización',
  // ── Loading Messages ──
  'loading.analyzingTask': 'Analizando descripción de la tarea…',
  'loading.analyzingTaskSub': 'IA generando pasos MOP y peligros\nImpulsado por la Base de Conocimiento de Su Empresa',
  'loading.identifyingHazards': 'Identificando peligros…',
  'loading.identifyingHazardsSub': 'IA analizando pasos y condiciones\nImpulsado por la Base de Conocimiento de Su Empresa',
  'loading.generatingControls': 'Generando controles…',
  'loading.generatingControlsSub': 'IA creando controles de seguridad para peligros aprobados\nImpulsado por la Base de Conocimiento de Su Empresa',
  'loading.analyzingPTP': 'Analizando Plan Pre-Tarea…',
  'loading.analyzingPTPSub': 'IA identificando riesgos clave y prioridades de seguridad\nImpulsado por la Base de Conocimiento de Su Empresa',
  'loading.creatingPTP': 'Creando Plan Pre-Tarea…',
  'loading.creatingPTPSub': 'Compilando documento de seguridad\nImpulsado por la Base de Conocimiento de Su Empresa',
  'loading.analyzingMeeting': 'Analizando grabación de la reunión…',
  'loading.analyzingMeetingSub': 'Evaluando calidad de la discusión del equipo\nImpulsado por la Base de Conocimiento de Su Empresa',
  'loading.extractingInfo': 'Extrayendo nueva información…',
  'loading.extractingInfoSub': 'Comparando notas de la reunión con el PTP\nImpulsado por la Base de Conocimiento de Su Empresa',
  'loading.updatingPTP': 'Actualizando Plan Pre-Tarea…',
  'loading.updatingPTPSub': 'Incorporando hallazgos de la reunión\nImpulsado por la Base de Conocimiento de Su Empresa',
  'loading.finalizingPTP': 'Finalizando Plan Pre-Tarea…',
  'loading.finalizingPTPSub': 'Guardando plan completado\nImpulsado por la Base de Conocimiento de Su Empresa',
  'loading.analyzingConditions': 'Analizando condiciones de la tarea…',
  'loading.analyzingConditionsSub': 'IA identificando condiciones E/C/M relevantes según su tarea',
  'loading.analyzingPermits': 'Analizando permisos requeridos…',
  'loading.analyzingPermitsSub': 'IA identificando permisos según su tarea\nImpulsado por la Base de Conocimiento de Su Empresa',
  // ── New PTP Screen ──
  'newPtp.title': 'Nuevo Plan Pre-Tarea',
  'newPtp.subtitle': 'Inicie un nuevo plan de seguridad para su cuadrilla',
  'newPtp.project': 'Proyecto',
  'newPtp.location': 'Ubicación',
  'newPtp.date': 'Fecha',
  'newPtp.startPlan': 'Iniciar Plan',
  'newPtp.poweredBy': 'Impulsado por la Base de Conocimiento de Su Empresa',
  'newPtp.whatToExpect': 'Qué esperar',
  'newPtp.step1': 'Describa la tarea usando su voz',
  'newPtp.step2': 'Revise pasos MOP, equipos y permisos',
  'newPtp.step3': 'Evalúe las condiciones del entorno de trabajo',
  'newPtp.step4': 'Asigne roles de cuadrilla',
  'newPtp.step5': 'Identifique peligros y medidas de control',
  'newPtp.step6': 'Revise y finalice el PTP',
  // ── Voice Description Screen ──
  'voice.title': 'Describa el Trabajo',
  'voice.subtitle': 'Describa la tarea, dónde se realizará y cuándo está programada. Incluya las condiciones del sitio y cómo se llevará a cabo el trabajo.',
  'voice.example': 'ej. "Vamos a instalar vigas de acero en el Nivel 2 ala este, comenzando a las 7 AM, usando la grúa móvil…"',
  'voice.placeholder': 'Describa la tarea, ubicación, hora y actividades clave...',
  'voice.analyzeTask': 'Analizar Tarea',
  'voice.tapToRecord': 'Toque para grabar',
  'voice.tapToStop': 'Escuchando… toque para detener',
  'voice.tapToReRecord': 'Toque para re-grabar',
  'voice.analyzing': 'Analizando…',
  'voice.modeVoice': 'Voz',
  'voice.modeType': 'Escribir',
  'voice.modeUpload': 'Subir',
  'voice.missingTitle': 'Información faltante',
  'voice.missingPrefix': 'Su descripción no incluye:',
  'voice.missingSuffix': '. Agregue más detalles para un mejor PTP.',
  'voice.addMoreInfo': 'Agregar Más Info',
  'voice.missingTask': 'descripción de la tarea',
  'voice.missingLocation': 'ubicación',
  'voice.missingTime': 'hora / horario',
  // ── Task Understanding Screen ──
  'task.mopSteps': 'Pasos MOP',
  'task.crew': 'Cuadrilla',
  'task.addStep': 'Agregar paso',
  'task.voiceCorrection': 'Describa cambios a los pasos MOP — agregar, eliminar, reordenar, o',
  'task.loadingSteps': 'IA identificando el tipo de tarea y generando pasos MOP',
  // ── Conditions Intro Screen ──
  'condIntro.title': 'Condiciones de la Tarea',
  'condIntro.subtitle': 'Antes de identificar peligros, evaluemos las condiciones usando el marco ECM.',
  'condIntro.environment': 'Entorno',
  'condIntro.environmentDesc': 'Condiciones físicas del sitio, clima, distribución, actividad cercana',
  'condIntro.capability': 'Capacidad',
  'condIntro.capabilityDesc': 'Experiencia del equipo, capacitación, certificaciones, familiaridad',
  'condIntro.motivation': 'Motivación',
  'condIntro.motivationDesc': 'Presión de horario, carga de trabajo, plazos, repetitividad de tareas',
  'condIntro.continue': 'Revisar Condiciones',
  // ── Work Environment Screen ──
  'workEnv.title': 'Condiciones de la Tarea',
  'workEnv.subtitle': 'Revise cada condición. Marque como relevante o no relevante para esta tarea.',
  'workEnv.reviewed': 'revisadas',
  'workEnv.addCondition': 'Agregar condición',
  'workEnv.relevant': 'Relevante',
  'workEnv.notRelevant': 'No Relevante',
  // ── Crew Assignment Screen ──
  'crew.title': 'Asignación de Cuadrilla',
  'crew.subtitle': 'Revise y ajuste la cuadrilla para esta tarea.',
  'crew.size': 'Tamaño de Cuadrilla',
  'crew.name': 'Nombre',
  'crew.role': 'Rol',
  'crew.responsibility': 'Responsabilidad',
  // ── Permits Screen ──
  'permits.title': 'Permisos Requeridos',
  'permits.subtitle': 'Revise los permisos requeridos para esta tarea.',
  'permits.noPermits': 'Sin permisos listados',
  'permits.addPermit': 'Agregar permiso',
  'permits.editPermit': 'Editar Permiso',
  'permits.voiceDesc': 'Describa cualquier permiso adicional requerido para esta tarea.',
  'permits.voiceCorrect': 'Corrección por Voz',
  'permits.extracting': 'Extrayendo permisos…',
  'permits.extracted': '¡Permisos extraídos!',
  'permits.analyzing': 'Analizando tarea para permisos…',
  'permits.analyzingSub': 'La IA está determinando los permisos requeridos basados en la tarea y el equipo.',
  'permits.needed': 'Necesario',
  'permits.notNeeded': 'No Necesario',
  'permits.permitStatus': 'Estado:',
  'permits.obtained': 'Obtenido',
  'permits.missing': 'Faltante',
  'permits.missingWarning': 'Permisos Requeridos Faltantes',
  // ── Hazard Review Screen ──
  'hazard.title': 'Revisar Peligros',
  'hazard.subtitle': 'Revise cada peligro. Marque como relevante o no relevante.',
  'hazard.reviewed': 'revisados',
  'hazard.generalHazards': 'Peligros Generales del Sitio',
  'hazard.addHazard': 'Agregar peligro',
  // ── Step Hazard / Controls Screen ──
  'controls.title': 'Controles',
  'controls.eliminateSub': 'Eliminar / Sustituir',
  'controls.engineering': 'Ingeniería',
  'controls.adminTraining': 'Admin / Capacitación',
  'controls.ppe': 'EPP',
  'controls.equipment': 'Equipos',
  // ── General Hazards Screen ──
  'general.title': 'Peligros Generales y Equipos',
  'general.equipment': 'Equipos Generales',
  // ── PTP Summary Screen ──
  'summary.title': 'Resumen del PTP',
  'summary.subtitle': 'Revise su Plan Pre-Tarea antes de crear.',
  'summary.createPTP': 'Crear PTP',
  'summary.mop': 'Método de Procedimiento (MOP)',
  'summary.conditions': 'Condiciones',
  'summary.crew': 'Cuadrilla',
  'summary.hazards': 'Peligros',
  'summary.controls': 'Controles',
  'summary.equipment': 'Equipos',
  // ── PTP Created / Complete ──
  'created.title': 'PTP Creado',
  'created.subtitle': 'Su Plan Pre-Tarea está listo.',
  'created.startMeeting': 'Iniciar Grabación de Reunión',
  'created.exportPTP': 'Exportar PTP',
  'created.qualityScore': 'Puntuación de Calidad',
  // ── Recording ──
  'recording.title': 'Grabación de Reunión',
  'recording.stop': 'Detener Grabación',
  'recording.pause': 'Pausar',
  'recording.resume': 'Reanudar',
  'recording.participants': 'Participantes',
  'recording.topicsCovered': 'Temas Cubiertos',
  // ── Quality Score ──
  'quality.title': 'Calidad de la Reunión',
  'quality.topicCoverage': 'Cobertura de Temas',
  'quality.participation': 'Participación del Equipo',
  'quality.duration': 'Duración de la Reunión',
  // ── New Info ──
  'newInfo.title': 'Nueva Información',
  'newInfo.subtitle': 'La IA encontró nueva información de la reunión que no estaba en el PTP original.',
  // ── Updated PTP ──
  'updatedPtp.title': 'Plan Pre-Tarea Actualizado',
  'updatedPtp.mopSteps': 'Pasos MOP — Peligros y Controles',
  'updatedPtp.continueQuiz': 'Continuar al Cuestionario',
  'updatedPtp.exportPTP': 'Exportar PTP',
  'updatedPtp.meetingReview': 'Revisión de Reunión',
  // ── Quiz ──
  'quiz.sendTitle': 'Enviar Cuestionario a la Cuadrilla',
  'quiz.sendButton': 'Enviar Cuestionario',
  'quiz.takeQuiz': 'Tomar Cuestionario como Capataz',
  'quiz.title': 'Cuestionario de Seguridad PTP',
  'quiz.complete': 'Cuestionario Completo',
  'quiz.next': 'Siguiente',
  'quiz.confirm': 'Confirmar',
  // ── Crew Results ──
  'results.title': 'Resultados del Cuestionario',
  'results.crewReadiness': 'Preparación de la Cuadrilla',
  'results.finalize': 'Finalizar PTP',
  'results.reviewPTP': 'Revisar PTP Actualizado',
  'results.repeatQuiz': 'Repetir Cuestionario de Seguridad',
  // ── PTP List ──
  'list.title': 'Planes Pre-Tarea',
  'list.plans': 'Planes',
  'list.dashboard': 'Panel',
  'list.createNew': 'Crear Nuevo PTP',
  'list.totalPtps': 'Total PTPs',
  'list.completed': 'Completados',
  'list.avgScore': 'Puntuación Promedio',
  'status.jobDone': 'Trabajo Terminado',
  'status.ptpComplete': 'PTP Completo',
  'status.inProgress': 'En Progreso',
  'status.draft': 'Borrador',
  'list.backToPlans': 'Volver a Planes',
  'list.contractor': 'Contratista',
  'list.workers': 'trabajadores',
  'list.meetingScore': 'Puntuación de Reunión',
  'list.ptpProgress': 'Progreso del PTP',
  'list.ptpCreated': 'Plan Pre-Tarea creado',
  'list.meetingRecorded': 'Reunión PTP grabada',
  'list.quizCompleted': 'Cuestionario de cuadrilla completado',
  'list.taskCompleted': 'Tarea completada e informe post-trabajo',
  'list.needsMeetingMsg': 'Este PTP aún no se ha discutido con la cuadrilla. Grabe la reunión para verificar la comprensión y capturar información de seguridad adicional.',
  'list.needsQuizMsg': 'La reunión ha sido grabada. Envíe el cuestionario a la cuadrilla para verificar que todos entienden el plan de seguridad.',
  'list.canCompleteMsg': 'El proceso PTP está completo. Una vez terminado el trabajo, márquelo como completado para presentar un informe post-trabajo con análisis de seguridad de IA.',
  'list.jobDoneMsg': 'Esta tarea ha sido completada y el informe post-trabajo ha sido archivado. El análisis de IA está disponible para revisión.',
  'list.recordMeeting': 'Grabar Reunión PTP',
  'list.sendQuiz': 'Enviar Cuestionario',
  'list.markComplete': 'Marcar Tarea Completada',
  'list.viewFullPtp': 'Ver PTP Completo',
  'list.jobsDone': 'Trabajos Terminados',
  'list.justCompleted': 'Recién completado',
  'list.meetingNotRecorded': 'Reunión no grabada',
  'list.readyForCompletion': 'Listo para completar tarea',
  // ── Dashboard ──
  'dash.title': 'Panel del Proyecto',
  'dash.projectSummary': 'Resumen del Proyecto',
  'dash.contractors': 'Contratistas',
  'dash.avgQuality': 'Calidad Promedio',
  'dash.meetingQuality': 'Calidad de Reunión',
  'dash.quizCompletion': 'Completación de Cuestionario',
  'dash.projectOverview': 'Resumen del Proyecto',
  'dash.last14Days': 'Últimos 14 Días',
  'dash.avgPtpQuality': 'Puntuación Promedio de Calidad PTP',
  'dash.downward': 'En Descenso',
  'dash.qualityExplanation': 'Impulsado por la disminución en la calidad de reuniones y completación de cuestionarios en contratistas de menor rendimiento',
  'dash.activeContractors': 'Contratistas Activos',
  'dash.contractorPerformance': 'Rendimiento de Contratistas',
  'dash.hazardCoverage': 'Cobertura de Peligros',
  'dash.controlQuality': 'Calidad de Controles',
  'dash.crewParticipation': 'Participación de Cuadrilla',
  'dash.updateRate': 'Tasa de Actualización',
  'dash.ptpsSubmitted': 'PTPs Enviados',
  'dash.trendDetail': 'Detalle de Tendencia',
  'dash.aiInsight': 'Análisis de IA',
  'dash.suggestedAction': 'Acción Sugerida',
  // ── Nearby Task Alert ──
  'nearby.title': 'Actividad Cercana Detectada',
  'nearby.subtitle': 'Otra tarea está programada cerca al mismo tiempo',
  'nearby.addToConditions': 'Agregar a Condiciones',
  'nearby.willBeAdded': 'Esto se agregará como condición de Entorno en su PTP para asegurar la coordinación adecuada.',
  // ── Export ──
  'export.preview': 'Vista Previa Pre-Turno',
  'export.close': 'Cerrar',
  // ── Signature ──
  'signature.title': 'Firma del Capataz',
  'signature.disclaimer': 'Al firmar a continuación, confirmo que he revisado este Plan Pre-Tarea, que todos los peligros y controles identificados son precisos, y que comunicaré este plan a todos los miembros de la cuadrilla antes de comenzar el trabajo.',
  'signature.placeholder': 'Firme aquí',
  'signature.clear': 'Limpiar',
  'signature.required': 'Se requiere firma para finalizar',
  'signature.finalize': 'Finalizar y Continuar',
  'signature.editPlan': 'Editar Plan',
  'signature.exportPdf': 'Exportar PDF',
  // ── Post-Job Review ──
  'postJob.title': 'Informe Post-Trabajo',
  // ── Date Labels ──
  'date.today': 'Hoy',
  'date.yesterday': 'Ayer',
  // ── Status Labels ──
  'status.all': 'Todos',
  'status.draft': 'Borrador',
  'status.ready': 'Listo',
  'status.signed': 'Firmado',
  'status.jobDone': 'Trabajo Hecho',
  // ── Inline Labels ──
  'list.results': 'Resultados',
  'list.noRecording': 'Sin Grabación',
  // ── Crew Screen ──
  'crew.addWorker': 'Agregar Trabajador',
  'crew.noMatch': 'Ningún trabajador coincide con',
  'crew.searchPlaceholder': 'Buscar por nombre o rol...',
  'crew.available': 'Trabajadores Disponibles',
  'crew.assigned': 'Asignado',
  'crew.added': 'Agregado',
  // ── Equipment Screen ──
  'equip.title': 'Equipos y Herramientas',
  'equip.noEquipment': 'Sin equipos listados',
  'equip.addItem': 'Agregar elemento',
  'equip.editEquipment': 'Editar Equipo',
  'equip.voiceDesc': 'Describa las herramientas y equipos necesarios.',
  'equip.voiceCorrect': 'Corrección por Voz',
  'equip.regenerate': 'Regenerar',
  'equip.extracting': 'Extrayendo equipos…',
  'equip.extracted': '¡Equipos extraídos!',
  // ── Permits Screen ──
  'permits.title': 'Permisos Requeridos',
  'permits.subtitle': 'Revise los permisos requeridos para esta tarea.',
  'permits.noPermits': 'Sin permisos listados',
  'permits.addPermit': 'Agregar permiso',
  'permits.editPermit': 'Editar Permiso',
  'permits.voiceDesc': 'Describa cualquier permiso adicional requerido para esta tarea.',
  'permits.voiceCorrect': 'Corrección por Voz',
  'permits.extracting': 'Extrayendo permisos…',
  'permits.extracted': '¡Permisos extraídos!',
  'permits.analyzing': 'Analizando tarea para permisos…',
  'permits.analyzingSub': 'La IA está determinando los permisos requeridos basados en la tarea y el equipo.',
  'permits.needed': 'Necesario',
  'permits.notNeeded': 'No Necesario',
  'permits.permitStatus': 'Estado:',
  'permits.obtained': 'Obtenido',
  'permits.missing': 'Faltante',
  'permits.missingWarning': 'Permisos Requeridos Faltantes',
  // ── Hazard Screen ──
  'hazard.addHazards': 'Agregar Peligros',
  'hazard.editHazard': 'Editar Peligro',
  'hazard.describePlaceholder': 'Describa el peligro...',
  'hazard.voiceDesc': 'Describa los peligros que ha identificado. Mencione el número de paso si aplica.',
  'hazard.noHazards': 'Sin peligros para este paso',
  'hazard.extracted': '¡Peligros extraídos!',
  // ── Summary Screen ──
  'summary.keyPoints': 'Puntos Clave',
  'summary.aiComplete': 'Análisis de IA completo',
  // ── Recording Screen ──
  'recording.listening': 'Escuchando...',
  'recording.aiNote': 'Nota de IA',
  'recording.meetingActive': 'La reunión está activa. Los temas se rastrearán a medida que se discutan.',
  'recording.pending': 'Pendiente',
  // ── General ──
  'general.none': 'Ninguno',
  'general.step': 'Paso'
};